'use client';
import React, { useState, useEffect } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { Camera, MessageSquare, Mic, Video, ArrowLeft, Check, Send, RefreshCw, Play, Square, Home, Plus, X } from 'lucide-react';
import AppImage from '@/components/ui/AppImage';
import { ANDRE_PHOTO_URL } from '@/lib/familyDemoData';
import { useFamilyDemo } from '@/lib/familyDemoState';
import { toast } from 'sonner';
import Icon from '@/components/ui/AppIcon';


type SendView = 'hub' | 'photo-pick' | 'photo-preview' | 'photo-confirm' | 'voice-record' | 'voice-confirm' | 'message-compose' | 'message-confirm';
type VoiceState = 'idle' | 'recording' | 'recorded' | 'playing' | 'sending' | 'sent' | 'error';

const MOCK_PHOTOS = [
  { id: 'photo-mock-1', src: 'https://images.unsplash.com/photo-1555469163-a69b6f2fc32d', alt: 'Coucher de soleil sur la mer avec ciel orange et rose, paysage de Martinique' },
  { id: 'photo-mock-2', src: 'https://images.unsplash.com/photo-1598873759285-acbb9dac3702', alt: 'Fleurs de jardin colorées, roses et blanches, dans un jardin ensoleillé' },
  { id: 'photo-mock-3', src: 'https://img.rocket.new/generatedImages/rocket_gen_img_1925c0e81-1774402657065.png', alt: 'Repas de famille à table, plats créoles, ambiance chaleureuse' },
  { id: 'photo-mock-4', src: 'https://images.unsplash.com/photo-1625147296031-6fd7aae9ab0a', alt: 'Paysage tropical de Martinique, palmiers et ciel bleu' },
  { id: 'photo-mock-5', src: 'https://images.unsplash.com/photo-1696797965359-f554cab95dc4', alt: 'Vue aérienne de la baie avec bateaux et eau turquoise' },
  { id: 'photo-mock-6', src: 'https://img.rocket.new/generatedImages/rocket_gen_img_1bc8e5f7f-1767107199150.png', alt: 'Portrait souriant d\'une famille réunie autour d\'une table' },
];

export default function SendScreenClient() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { addActivity, addMessage } = useFamilyDemo();
  const initialAction = searchParams.get('action');

  const [view, setView] = useState<SendView>(
    initialAction === 'photo' ? 'photo-pick' :
    initialAction === 'voice' ? 'voice-record' :
    initialAction === 'message'? 'message-compose' : 'hub'
  );
  const [selectedPhoto, setSelectedPhoto] = useState<typeof MOCK_PHOTOS[0] | null>(null);
  const [caption, setCaption] = useState('');
  const [messageText, setMessageText] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);

  // Voice recording state machine
  const [voiceState, setVoiceState] = useState<VoiceState>('idle');
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const recordTimerRef = React.useRef<ReturnType<typeof setInterval> | null>(null);
  const playTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (voiceState === 'recording') {
      recordTimerRef.current = setInterval(() => {
        setRecordingSeconds((prev) => prev + 1);
      }, 1000);
    } else {
      if (recordTimerRef.current) clearInterval(recordTimerRef.current);
    }
    return () => {
      if (recordTimerRef.current) clearInterval(recordTimerRef.current);
    };
  }, [voiceState]);

  const formatRecordTime = (s: number) => {
    const m = Math.floor(s / 60).toString().padStart(2, '0');
    const sec = (s % 60).toString().padStart(2, '0');
    return `${m}:${sec}`;
  };

  const startRecording = () => {
    setVoiceState('recording');
    setRecordingSeconds(0);
    setIsPlaying(false);
  };

  const stopRecording = () => {
    if (recordingSeconds > 0) {
      setVoiceState('recorded');
    } else {
      setVoiceState('idle');
    }
  };

  const resetRecording = () => {
    setVoiceState('idle');
    setRecordingSeconds(0);
    setIsPlaying(false);
    if (playTimerRef.current) clearTimeout(playTimerRef.current);
  };

  const handlePlayback = () => {
    if (isPlaying) {
      setIsPlaying(false);
      setVoiceState('recorded');
      if (playTimerRef.current) clearTimeout(playTimerRef.current);
    } else {
      setIsPlaying(true);
      setVoiceState('playing');
      playTimerRef.current = setTimeout(() => {
        setIsPlaying(false);
        setVoiceState('recorded');
      }, recordingSeconds * 1000);
    }
  };

  const simulateSend = async (onDone: () => void) => {
    setIsSending(true);
    await new Promise((r) => setTimeout(r, 1200));
    setIsSending(false);
    onDone();
  };

  const handleSendPhoto = () => {
    simulateSend(() => {
      const now = new Date().toISOString();
      addActivity({
        id: `act-photo-${Date.now()}`,
        seniorId: 'andre',
        actorId: 'marie',
        type: 'photo',
        title: caption
          ? `Vous avez envoyé une photo à André : "${caption}"`
          : 'Vous avez envoyé une photo à André',
        createdAt: now,
        status: 'informational',
        // Legacy compat
        category: 'photo',
        action: 'photo_sent',
        label: caption
          ? `Vous avez envoyé une photo à André : "${caption}"`
          : 'Vous avez envoyé une photo à André',
        contactId: 'marie',
      });
      addMessage({
        id: `msg-photo-${Date.now()}`,
        conversationId: 'conv-andre-marie',
        senderId: 'marie',
        recipientId: 'andre',
        type: 'photo',
        text: caption || '',
        mediaUrl: selectedPhoto?.src ?? '',
        thumbnailUrl: selectedPhoto?.src ?? '',
        createdAt: now,
        status: 'sent',
        // Legacy compat
        contactId: 'marie',
        seniorId: 'andre',
        content: caption || '',
        sentAt: now,
        readStatus: 'sent',
        direction: 'outgoing',
      });
      setView('photo-confirm');
    });
  };

  const handleSendVoice = () => {
    setVoiceState('sending');
    simulateSend(() => {
      const dur = formatRecordTime(recordingSeconds);
      const now = new Date().toISOString();
      addActivity({
        id: `act-voice-${Date.now()}`,
        seniorId: 'andre',
        actorId: 'marie',
        type: 'message',
        title: `Vous avez envoyé un message vocal à André (${dur})`,
        createdAt: now,
        status: 'informational',
        // Legacy compat
        category: 'message',
        action: 'voice_sent',
        label: `Vous avez envoyé un message vocal à André (${dur})`,
        contactId: 'marie',
      });
      addMessage({
        id: `msg-voice-${Date.now()}`,
        conversationId: 'conv-andre-marie',
        senderId: 'marie',
        recipientId: 'andre',
        type: 'voice',
        text: `Message vocal — ${dur}`,
        durationSeconds: recordingSeconds,
        createdAt: now,
        status: 'sent',
        // Legacy compat
        contactId: 'marie',
        seniorId: 'andre',
        content: `Message vocal — ${dur}`,
        sentAt: now,
        readStatus: 'sent',
        direction: 'outgoing',
      });
      setVoiceState('sent');
      setView('voice-confirm');
    });
  };

  const handleSendMessage = () => {
    if (!messageText.trim()) return;
    simulateSend(() => {
      const now = new Date().toISOString();
      addActivity({
        id: `act-msg-${Date.now()}`,
        seniorId: 'andre',
        actorId: 'marie',
        type: 'message',
        title: 'Vous avez envoyé un message à André',
        createdAt: now,
        status: 'informational',
        // Legacy compat
        category: 'message',
        action: 'text_sent',
        label: 'Vous avez envoyé un message à André',
        contactId: 'marie',
      });
      addMessage({
        id: `msg-text-${Date.now()}`,
        conversationId: 'conv-andre-marie',
        senderId: 'marie',
        recipientId: 'andre',
        type: 'text',
        text: messageText.trim(),
        createdAt: now,
        status: 'sent',
        // Legacy compat
        contactId: 'marie',
        seniorId: 'andre',
        content: messageText.trim(),
        sentAt: now,
        readStatus: 'sent',
        direction: 'outgoing',
      });
      toast.success('Message envoyé à André');
      setView('message-confirm');
    });
  };

  const SEND_CARDS = [
    {
      id: 'send-photo',
      icon: Camera,
      title: 'Envoyer une photo',
      description: 'Partagez un moment avec André',
      color: 'text-lucat-teal',
      bg: 'bg-lucat-teal-light',
      border: 'border-lucat-teal/20',
      badge: null,
      action: () => setView('photo-pick'),
    },
    {
      id: 'send-message',
      icon: MessageSquare,
      title: 'Envoyer un message',
      description: 'Écrivez quelques mots à André',
      color: 'text-lucat-blue',
      bg: 'bg-lucat-blue-light',
      border: 'border-lucat-blue/20',
      badge: null,
      action: () => setView('message-compose'),
    },
    {
      id: 'send-voice',
      icon: Mic,
      title: 'Message vocal',
      description: 'Enregistrez votre voix',
      color: 'text-lucat-purple',
      bg: 'bg-lucat-purple-light',
      border: 'border-lucat-purple/20',
      badge: null,
      action: () => setView('voice-record'),
    },
    {
      id: 'send-video',
      icon: Video,
      title: 'Courte vidéo',
      description: 'Partagez un instant en vidéo',
      color: 'text-lucat-orange',
      bg: 'bg-lucat-orange-light',
      border: 'border-lucat-orange/20',
      badge: 'Bientôt',
      action: () => setShowVideoModal(true),
    },
  ];

  // ── Hub ──
  if (view === 'hub') {
    return (
      <div className="pb-4">
        <div className="bg-lucat-navy px-4 py-4">
          <h1 className="text-white text-2xl font-bold">Envoyer</h1>
          <p className="text-white/60 text-sm mt-0.5">Que souhaitez-vous partager avec André ?</p>
        </div>
        <div className="px-4 pt-4 grid grid-cols-2 gap-3">
          {SEND_CARDS.map((card) => {
            const Icon = card.icon;
            const isDisabled = card.id === 'send-video';
            return (
              <button
                key={card.id}
                onClick={card.action}
                className={`flex flex-col gap-3 rounded-2xl p-4 border ${card.bg} ${card.border} shadow-card tap-target active:scale-95 transition-all duration-150 text-left relative ${isDisabled ? 'opacity-70' : ''}`}
              >
                {card.badge && (
                  <span className="absolute top-2.5 right-2.5 bg-lucat-orange text-white text-[10px] font-bold rounded-full px-2 py-0.5">
                    {card.badge}
                  </span>
                )}
                <div className="w-12 h-12 rounded-xl bg-white/60 flex items-center justify-center">
                  <Icon size={24} className={card.color} />
                </div>
                <div>
                  <p className="text-lucat-navy font-bold text-[14px] leading-tight">{card.title}</p>
                  <p className="text-muted-foreground text-xs mt-1 leading-snug">{card.description}</p>
                </div>
              </button>
            );
          })}
        </div>

        {/* Short video "Bientôt disponible" modal */}
        {showVideoModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-6">
            <div className="bg-card rounded-2xl p-6 w-full max-w-sm shadow-call fade-in">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lucat-navy font-bold text-lg">Bientôt disponible</h2>
                <button
                  onClick={() => setShowVideoModal(false)}
                  className="w-8 h-8 rounded-full bg-muted flex items-center justify-center tap-target"
                  aria-label="Fermer"
                >
                  <X size={16} className="text-muted-foreground" />
                </button>
              </div>
              <p className="text-muted-foreground text-sm leading-relaxed">
                L&apos;envoi de vidéo sera disponible prochainement.
              </p>
              <button
                onClick={() => setShowVideoModal(false)}
                className="mt-5 w-full bg-lucat-navy text-white rounded-xl py-3 text-sm font-bold tap-target active:scale-95 transition-all duration-150"
              >
                Fermer
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── Photo Pick ──
  if (view === 'photo-pick') {
    return (
      <div className="pb-4">
        <div className="bg-lucat-navy px-4 py-3 flex items-center gap-3">
          <button onClick={() => setView('hub')} className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-white/10 tap-target" aria-label="Retour">
            <ArrowLeft size={20} className="text-white" />
          </button>
          <h2 className="text-white font-bold text-lg">Choisir une photo</h2>
        </div>
        <div className="px-4 pt-4">
          <p className="text-muted-foreground text-sm mb-3">Sélectionnez une photo à envoyer à André</p>
          <div className="grid grid-cols-3 gap-2">
            {MOCK_PHOTOS.map((photo) => (
              <button
                key={photo.id}
                onClick={() => { setSelectedPhoto(photo); setView('photo-preview'); }}
                className={`aspect-square rounded-xl overflow-hidden border-2 transition-all duration-150 active:scale-95 ${selectedPhoto?.id === photo.id ? 'border-lucat-green' : 'border-transparent'}`}
              >
                <AppImage
                  src={photo.src}
                  alt={photo.alt}
                  width={120}
                  height={120}
                  className="w-full h-full object-cover"
                />
              </button>
            ))}
          </div>
          <div className="mt-4">
            <button
              onClick={() => { setSelectedPhoto(MOCK_PHOTOS[0]); setView('photo-preview'); }}
              className="w-full border-2 border-dashed border-border rounded-2xl py-4 flex items-center justify-center gap-2 text-muted-foreground text-sm font-medium tap-target active:scale-95 transition-all duration-150"
            >
              <Plus size={18} />
              Utiliser l&apos;appareil photo
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Photo Preview ──
  if (view === 'photo-preview' && selectedPhoto) {
    return (
      <div className="pb-4">
        <div className="bg-lucat-navy px-4 py-3 flex items-center gap-3">
          <button onClick={() => setView('photo-pick')} className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-white/10 tap-target" aria-label="Retour">
            <ArrowLeft size={20} className="text-white" />
          </button>
          <h2 className="text-white font-bold text-lg">Aperçu</h2>
        </div>
        <div className="px-4 pt-4 flex flex-col gap-4">
          <div className="rounded-2xl overflow-hidden shadow-card">
            <AppImage
              src={selectedPhoto.src}
              alt={selectedPhoto.alt}
              width={390}
              height={260}
              className="w-full object-cover"
            />
          </div>

          <div>
            <label className="block text-lucat-navy font-semibold text-sm mb-1.5" htmlFor="caption">
              Ajouter un petit mot <span className="text-muted-foreground font-normal">(facultatif)</span>
            </label>
            <textarea
              id="caption"
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              placeholder="Bonjour Papa, regardez ce beau coucher de soleil !"
              className="w-full bg-input rounded-xl px-4 py-3 text-foreground text-sm resize-none border border-border focus:outline-none focus:border-lucat-green transition-colors"
              rows={3}
            />
          </div>

          <div className="flex flex-col gap-2.5">
            <button
              onClick={handleSendPhoto}
              disabled={isSending}
              className="w-full bg-lucat-green text-white rounded-2xl py-4 text-base font-bold tap-target active:scale-95 transition-all duration-150 flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {isSending
                ? <><span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />Envoi en cours…</>
                : <><Send size={18} />Envoyer à André</>
              }
            </button>
            <button onClick={() => setView('photo-pick')} className="w-full bg-muted text-muted-foreground rounded-2xl py-3 text-sm font-semibold tap-target active:scale-95 transition-all duration-150">
              Choisir une autre photo
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Photo Confirm ──
  if (view === 'photo-confirm') {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center px-6 fade-in">
        <div className="w-20 h-20 rounded-full bg-lucat-green-light flex items-center justify-center mb-6">
          <Check size={36} className="text-lucat-green" />
        </div>
        <h1 className="text-lucat-navy text-2xl font-bold text-center">Photo envoyée !</h1>
        <p className="text-muted-foreground text-base text-center mt-3 leading-relaxed">
          Votre photo a bien été envoyée à André.<br />
          <span className="font-medium text-lucat-teal">André la verra sur sa tablette LUCAT CONNECT.</span>
        </p>
        {selectedPhoto && (
          <div className="w-full max-w-xs rounded-2xl overflow-hidden shadow-card mt-5">
            <AppImage
              src={selectedPhoto.src}
              alt={selectedPhoto.alt}
              width={320}
              height={180}
              className="w-full object-cover"
            />
          </div>
        )}
        <div className="flex flex-col gap-3 w-full mt-8">
          <button
            onClick={() => { setSelectedPhoto(null); setCaption(''); setView('photo-pick'); }}
            className="w-full bg-lucat-teal text-white rounded-2xl py-4 text-base font-bold tap-target active:scale-95 transition-all duration-150 flex items-center justify-center gap-2"
          >
            <Camera size={18} />
            Envoyer une autre photo
          </button>
          <button
            onClick={() => router.push('/')}
            className="w-full bg-muted text-muted-foreground rounded-2xl py-4 text-base font-semibold tap-target active:scale-95 transition-all duration-150 flex items-center justify-center gap-2"
          >
            <Home size={18} />
            Revenir à l&apos;accueil
          </button>
        </div>
      </div>
    );
  }

  // ── Voice Record ──
  if (view === 'voice-record') {
    const isRecording = voiceState === 'recording';
    const hasRecording = voiceState === 'recorded' || voiceState === 'playing';
    const isSendingVoice = voiceState === 'sending';

    return (
      <div className="pb-4">
        <div className="bg-lucat-navy px-4 py-3 flex items-center gap-3">
          <button onClick={() => { resetRecording(); setView('hub'); }} className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-white/10 tap-target" aria-label="Retour">
            <ArrowLeft size={20} className="text-white" />
          </button>
          <h2 className="text-white font-bold text-lg">Message vocal</h2>
        </div>

        <div className="flex flex-col items-center px-6 pt-8 pb-8 gap-6">
          {/* André portrait — replaces grey rectangle */}
          <div className="flex flex-col items-center gap-3">
            <div className="w-20 h-20 rounded-2xl overflow-hidden border-3 border-lucat-purple/40 shadow-card" style={{ borderWidth: '3px' }}>
              <AppImage
                src={ANDRE_PHOTO_URL}
                alt="André Beaumont, senior souriant, destinataire du message vocal"
                width={80}
                height={80}
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="text-center">
              <p className="text-lucat-navy font-bold text-base">André</p>
              <p className="text-muted-foreground text-xs">Mon père</p>
            </div>
          </div>

          {/* Record button */}
          <div className="flex flex-col items-center gap-3">
            <button
              onClick={isRecording ? stopRecording : hasRecording ? undefined : startRecording}
              disabled={hasRecording && !isRecording}
              className={`
                w-24 h-24 rounded-full flex items-center justify-center transition-all duration-150 active:scale-95 tap-target
                ${isRecording ? 'bg-lucat-red record-pulse' : hasRecording ? 'bg-muted cursor-not-allowed' : 'bg-lucat-purple shadow-call'}
              `}
              aria-label={isRecording ? "Arrêter l'enregistrement" : "Commencer l'enregistrement"}
            >
              {isRecording
                ? <Square size={32} className="text-white" />
                : <Mic size={36} className={hasRecording ? 'text-muted-foreground' : 'text-white'} />
              }
            </button>

            <div className="text-center">
              {voiceState === 'idle' && (
                <p className="text-muted-foreground text-sm">Appuyez pour enregistrer</p>
              )}
              {voiceState === 'recording' && (
                <p className="text-lucat-red font-bold text-base flex items-center gap-2">
                  <span className="w-2 h-2 bg-lucat-red rounded-full animate-pulse" />
                  Enregistrement en cours…
                </p>
              )}
              {(voiceState === 'recorded' || voiceState === 'playing') && (
                <p className="text-lucat-purple font-semibold text-sm">Enregistrement terminé</p>
              )}
              {voiceState === 'sending' && (
                <p className="text-lucat-purple font-semibold text-sm">Envoi en cours…</p>
              )}
              <p className="text-lucat-navy font-bold text-2xl tabular-nums mt-1">{formatRecordTime(recordingSeconds)}</p>
            </div>
          </div>

          {/* Playback & reset controls */}
          {hasRecording && (
            <div className="flex gap-3 w-full slide-up">
              <button
                onClick={handlePlayback}
                className="flex-1 bg-lucat-purple-light text-lucat-purple rounded-xl py-3 text-sm font-semibold tap-target active:scale-95 transition-all duration-150 flex items-center justify-center gap-1.5 border border-lucat-purple/20"
              >
                {isPlaying ? <><Square size={14} />Arrêter</> : <><Play size={14} />Écouter</>}
              </button>
              <button
                onClick={resetRecording}
                className="flex-1 bg-muted text-muted-foreground rounded-xl py-3 text-sm font-semibold tap-target active:scale-95 transition-all duration-150 flex items-center justify-center gap-1.5"
              >
                <RefreshCw size={14} />
                Recommencer
              </button>
            </div>
          )}

          {hasRecording && (
            <button
              onClick={handleSendVoice}
              disabled={isSendingVoice}
              className="w-full bg-lucat-purple text-white rounded-2xl py-4 text-base font-bold tap-target active:scale-95 transition-all duration-150 flex items-center justify-center gap-2 disabled:opacity-60 shadow-card slide-up"
            >
              {isSendingVoice
                ? <><span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />Envoi en cours…</>
                : <><Send size={18} />Envoyer à André</>
              }
            </button>
          )}
        </div>
      </div>
    );
  }

  // ── Voice Confirm ──
  if (view === 'voice-confirm') {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center px-6 fade-in">
        <div className="w-20 h-20 rounded-full bg-lucat-purple-light flex items-center justify-center mb-6">
          <Check size={36} className="text-lucat-purple" />
        </div>
        <h1 className="text-lucat-navy text-2xl font-bold text-center">Message vocal envoyé !</h1>
        <p className="text-muted-foreground text-base text-center mt-3 leading-relaxed">
          Votre message vocal a été envoyé à André.<br />
          <span className="font-medium text-lucat-purple">André pourra l&apos;écouter sur sa tablette.</span>
        </p>
        <div className="flex flex-col gap-3 w-full mt-10">
          <button
            onClick={() => { resetRecording(); setView('voice-record'); }}
            className="w-full bg-lucat-purple text-white rounded-2xl py-4 text-base font-bold tap-target active:scale-95 transition-all duration-150 flex items-center justify-center gap-2"
          >
            <Mic size={18} />
            Enregistrer un autre message
          </button>
          <button
            onClick={() => router.push('/')}
            className="w-full bg-muted text-muted-foreground rounded-2xl py-4 text-base font-semibold tap-target active:scale-95 transition-all duration-150 flex items-center justify-center gap-2"
          >
            <Home size={18} />
            Revenir à l&apos;accueil
          </button>
        </div>
      </div>
    );
  }

  // ── Message Compose ──
  if (view === 'message-compose') {
    const canSend = messageText.trim().length > 0;
    return (
      <div className="pb-4">
        <div className="bg-lucat-navy px-4 py-3 flex items-center gap-3">
          <button onClick={() => setView('hub')} className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-white/10 tap-target" aria-label="Retour">
            <ArrowLeft size={20} className="text-white" />
          </button>
          <h2 className="text-white font-bold text-lg">Envoyer un message</h2>
        </div>
        <div className="px-4 pt-4 flex flex-col gap-4">
          <div className="flex items-center gap-3 bg-card rounded-2xl p-3 border border-border">
            <div className="w-12 h-12 rounded-xl overflow-hidden flex-shrink-0">
              <AppImage
                src={ANDRE_PHOTO_URL}
                alt="André Beaumont, senior souriant, destinataire du message"
                width={48}
                height={48}
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div>
              <p className="text-lucat-navy font-bold text-base">André</p>
              <p className="text-muted-foreground text-xs">Mon père</p>
            </div>
          </div>

          <div>
            <label className="block text-lucat-navy font-semibold text-sm mb-1.5" htmlFor="message-text">
              Votre message
            </label>
            <textarea
              id="message-text"
              value={messageText}
              onChange={(e) => setMessageText(e.target.value)}
              placeholder="Bonjour Papa, comment vas-tu aujourd'hui ?"
              className="w-full bg-input rounded-xl px-4 py-3 text-foreground text-sm resize-none border border-border focus:outline-none focus:border-lucat-blue transition-colors"
              rows={5}
            />
            <p className="text-muted-foreground text-xs mt-1 text-right">{messageText.length} caractère{messageText.length !== 1 ? 's' : ''}</p>
          </div>

          <button
            onClick={handleSendMessage}
            disabled={!canSend || isSending}
            className={`w-full rounded-2xl py-4 text-base font-bold tap-target active:scale-95 transition-all duration-150 flex items-center justify-center gap-2 shadow-card ${canSend ? 'bg-lucat-blue text-white' : 'bg-muted text-muted-foreground cursor-not-allowed'}`}
          >
            {isSending
              ? <><span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />Envoi en cours…</>
              : <><Send size={18} />Envoyer à André</>
            }
          </button>
        </div>
      </div>
    );
  }

  // ── Message Confirm ──
  if (view === 'message-confirm') {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center px-6 fade-in">
        <div className="w-20 h-20 rounded-full bg-lucat-blue-light flex items-center justify-center mb-6">
          <Check size={36} className="text-lucat-blue" />
        </div>
        <h1 className="text-lucat-navy text-2xl font-bold text-center">Message envoyé !</h1>
        <p className="text-muted-foreground text-base text-center mt-3 leading-relaxed">
          Votre message a bien été envoyé à André.<br />
          <span className="font-medium text-lucat-blue">André le lira sur sa tablette LUCAT CONNECT.</span>
        </p>
        <div className="flex flex-col gap-3 w-full mt-10">
          <button
            onClick={() => { setMessageText(''); setView('message-compose'); }}
            className="w-full bg-lucat-blue text-white rounded-2xl py-4 text-base font-bold tap-target active:scale-95 transition-all duration-150 flex items-center justify-center gap-2"
          >
            <MessageSquare size={18} />
            Envoyer un autre message
          </button>
          <button
            onClick={() => router.push('/')}
            className="w-full bg-muted text-muted-foreground rounded-2xl py-4 text-base font-semibold tap-target active:scale-95 transition-all duration-150 flex items-center justify-center gap-2"
          >
            <Home size={18} />
            Revenir à l&apos;accueil
          </button>
        </div>
      </div>
    );
  }

  return null;
}